import React from 'react';
import FeatherIcon from 'feather-icons-react';

/**
 * 팝업 미리보기 컴포넌트
 * - 5종 프리셋(text/image/coupon/cta/form)을 React로 렌더
 * - 유형(modal/toast/bar/sidebar/fullscreen)별 포지셔닝
 * - 디바이스 프레임(PC/모바일/태블릿) 시뮬레이션
 *
 * Props:
 *   popup: {type, position, width, height, bgColor, fgColor, accentColor,
 *           borderRadius, shadow, templateKey, templateData}
 *   device: 'pc' | 'mobile' | 'tablet'
 *   scale: number (0.4 등) — 미리보기 영역에서 축소
 */

export const DEVICE_FRAMES = {
  pc:     {label: 'PC',       w: 1280, h: 800,  icon: 'monitor'},
  mobile: {label: '모바일',    w: 375,  h: 667,  icon: 'smartphone'},
  tablet: {label: '태블릿',    w: 768,  h: 1024, icon: 'tablet'}
};

export const POPUP_TYPES = [
  {value: 'modal',      label: '중앙 모달',     icon: 'square'},
  {value: 'toast',      label: '토스트',        icon: 'bell'},
  {value: 'bar',        label: '상/하단 바',    icon: 'minus'},
  {value: 'sidebar',    label: '사이드 시트',   icon: 'sidebar'},
  {value: 'fullscreen', label: '풀스크린',      icon: 'maximize'}
];

export const POPUP_POSITIONS_BY_TYPE = {
  modal:      [{v: 'center', l: '중앙'}],
  toast:      [
    {v: 'top-left', l: '좌상'}, {v: 'top-right', l: '우상'},
    {v: 'bottom-left', l: '좌하'}, {v: 'bottom-right', l: '우하'}
  ],
  bar:        [{v: 'top-bar', l: '상단 바'}, {v: 'bottom-bar', l: '하단 바'}],
  sidebar:    [{v: 'left', l: '좌측'}, {v: 'right', l: '우측'}],
  fullscreen: [{v: 'center', l: '전체'}]
};

export const TEMPLATE_PRESETS = [
  {key: 'text',   label: '텍스트',     icon: 'type',         desc: '제목 + 본문 + 닫기'},
  {key: 'image',  label: '이미지',     icon: 'image',        desc: '이미지 + 캡션'},
  {key: 'coupon', label: '쿠폰',       icon: 'tag',          desc: '쿠폰 코드 + 다운로드 버튼'},
  {key: 'cta',    label: 'CTA 버튼',   icon: 'mouse-pointer', desc: '제목 + CTA 버튼'},
  {key: 'form',   label: '폼',         icon: 'edit-3',       desc: '이메일 수집'}
];

export function defaultTemplateData(key) {
  switch (key) {
    case 'text':   return {heading: '환영합니다', body: '온리플 쇼핑몰에 오신 것을 환영합니다.', closeLabel: '닫기'};
    case 'image':  return {imageUrl: '', caption: '봄 시즌 신상품', linkUrl: ''};
    case 'coupon': return {heading: '신규 가입 쿠폰', code: 'WELCOME10', body: '회원 가입 시 10% 할인', buttonLabel: '쿠폰 받기', buttonUrl: ''};
    case 'cta':    return {heading: '오늘 하루 특별 할인', body: '50% 세일 진행 중', buttonLabel: '지금 보러가기', buttonUrl: ''};
    case 'form':   return {heading: '뉴스레터 구독', body: '이메일을 등록하면 신상품 소식을 보내드립니다.', placeholder: 'you@example.com', buttonLabel: '구독하기'};
    default: return {};
  }
}

const sizeStyle = (popup) => {
  const w = popup.width && popup.width.value > 0 ? `${popup.width.value}${popup.width.unit || 'px'}` : '480px';
  const h = popup.height && popup.height.value > 0 ? `${popup.height.value}${popup.height.unit || 'px'}` : 'auto';
  return {width: w, height: h};
};

const shadowStyle = (level) => {
  switch (level) {
    case 'sm': return '0 2px 6px rgba(0,0,0,0.1)';
    case 'md': return '0 6px 16px rgba(0,0,0,0.12)';
    case 'lg': return '0 12px 36px rgba(0,0,0,0.18)';
    default: return 'none';
  }
};

// 위치별 포지셔닝
const positionStyle = (popup) => {
  const base = {position: 'absolute', boxShadow: shadowStyle(popup.shadow)};
  switch (popup.type) {
    case 'modal':
      return {...base, top: '50%', left: '50%', transform: 'translate(-50%, -50%)'};
    case 'toast':
      switch (popup.position) {
        case 'top-left':     return {...base, top: 16, left: 16};
        case 'top-right':    return {...base, top: 16, right: 16};
        case 'bottom-left':  return {...base, bottom: 16, left: 16};
        case 'bottom-right':
        default:             return {...base, bottom: 16, right: 16};
      }
    case 'bar':
      if (popup.position === 'top-bar') return {...base, top: 0, left: 0, right: 0, width: '100%'};
      return {...base, bottom: 0, left: 0, right: 0, width: '100%'};
    case 'sidebar':
      if (popup.position === 'left') return {...base, top: 0, bottom: 0, left: 0, height: '100%'};
      return {...base, top: 0, bottom: 0, right: 0, height: '100%'};
    case 'fullscreen':
      return {...base, inset: 0, width: '100%', height: '100%', borderRadius: 0};
    default:
      return {...base, top: '50%', left: '50%', transform: 'translate(-50%, -50%)'};
  }
};

// 템플릿별 콘텐츠 렌더
const renderTemplate = (popup) => {
  const tk = popup.templateKey || 'text';
  const td = popup.templateData || {};
  const acc = popup.accentColor || '#4A6CF7';
  const fg = popup.fgColor || '#1F2937';
  const muted = 'rgba(0,0,0,0.55)';

  switch (tk) {
    case 'text':
      return (
        <div className="pop-tpl">
          <div className="pop-tpl__heading" style={{color: fg}}>{td.heading || '제목'}</div>
          <div className="pop-tpl__body" style={{color: muted}}>{td.body || '내용'}</div>
        </div>
      );
    case 'image':
      return (
        <div className="pop-tpl pop-tpl--image">
          {td.imageUrl
            ? <img src={td.imageUrl} alt=""/>
            : <div className="pop-tpl__imgph"><FeatherIcon icon="image" size="36"/><span>이미지를 등록하세요</span></div>
          }
          {/* 이미지로만 노출하기(imageOnly) 설정 시 캡션 미노출 */}
          {td.caption && !td.imageOnly && <div className="pop-tpl__caption" style={{color: td.captionColor || fg, ...(td.captionFontSize != null ? {fontSize: `${td.captionFontSize}px`} : {})}}>{td.caption}</div>}
        </div>
      );
    case 'coupon':
      return (
        <div className="pop-tpl pop-tpl--coupon">
          <div className="pop-tpl__heading" style={{color: fg}}>{td.heading || '쿠폰'}</div>
          <div className="pop-tpl__body" style={{color: muted}}>{td.body || ''}</div>
          <div className="pop-tpl__couponcode" style={{borderColor: acc, color: acc}}>{td.code || 'COUPON10'}</div>
          <button className="pop-tpl__btn" style={{background: acc}}>{td.buttonLabel || '쿠폰 받기'}</button>
        </div>
      );
    case 'cta':
      return (
        <div className="pop-tpl pop-tpl--cta">
          <div className="pop-tpl__heading" style={{color: fg}}>{td.heading || '제목'}</div>
          <div className="pop-tpl__body" style={{color: muted}}>{td.body || ''}</div>
          <button className="pop-tpl__btn" style={{background: acc}}>{td.buttonLabel || '자세히 보기'}</button>
        </div>
      );
    case 'form':
      return (
        <div className="pop-tpl pop-tpl--form">
          <div className="pop-tpl__heading" style={{color: fg}}>{td.heading || '구독'}</div>
          <div className="pop-tpl__body" style={{color: muted}}>{td.body || ''}</div>
          <div className="pop-tpl__formrow">
            <input type="email" placeholder={td.placeholder || 'you@example.com'} disabled/>
            <button className="pop-tpl__btn" style={{background: acc}}>{td.buttonLabel || '구독'}</button>
          </div>
        </div>
      );
    default:
      return null;
  }
};

const bgStyle = (popup) => {
  if (popup.bgColor && popup.bgColor.startsWith('linear-gradient')) {
    return {background: popup.bgColor};
  }
  return {background: popup.bgColor || '#FFFFFF'};
};

/**
 * 팝업 본체 (디바이스 프레임 안에 배치되는 팝업 자체)
 */
export const PopupBody = ({popup}) => {
  const isFlowType = popup.type === 'bar' || popup.type === 'fullscreen' || popup.type === 'sidebar';
  // 이미지 템플릿의 '이미지로만 노출하기' — 배경색을 무시(투명)하고 캡션·여백 없이 이미지만 노출
  const isImageOnly = popup.templateKey === 'image' && popup.templateData && !!popup.templateData.imageOnly;
  const style = {
    ...positionStyle(popup),
    ...(!isFlowType ? sizeStyle(popup) : {}),
    ...(isImageOnly ? {background: 'transparent'} : bgStyle(popup)),
    borderRadius: popup.type === 'bar' || popup.type === 'fullscreen' ? 0 : (popup.borderRadius != null ? popup.borderRadius : 12),
    overflow: 'hidden',
    // 설정 너비가 디바이스 프레임보다 넓어도 미리보기에서 양끝이 넘치지 않도록 프레임 폭으로 제한
    maxWidth: '100%'
  };
  return (
    <div className={`pop-popup${isImageOnly ? ' pop-popup--imageonly' : ''}`} style={style}>
      <button className="pop-popup__close" title="닫기">
        <FeatherIcon icon="x" size="14"/>
      </button>
      <div className="pop-popup__body">
        {renderTemplate(popup)}
      </div>
      {/* 이미지 전용 모드는 상단 X 가 이미지에 묻힐 수 있어 하단 바(보지 않기 + 닫기)를 항상 노출 */}
      {((popup.frequency && popup.frequency.showDismissBtn) || isImageOnly) && (
        <div className="pop-popup__dismiss">
          <label>
            <input type="checkbox" disabled/> 오늘 하루 보지 않기
          </label>
          {isImageOnly && <span className="pop-popup__dismissClose">닫기</span>}
        </div>
      )}
    </div>
  );
};

/**
 * 디바이스 프레임 + 팝업
 * scale: 외부에서 컨테이너 크기에 맞게 0.4 등 설정
 */
export const DeviceFrame = ({device, popup, scale = 1}) => {
  const frame = DEVICE_FRAMES[device] || DEVICE_FRAMES.pc;
  return (
    <div className={`pop-frame pop-frame--${device}`}
         style={{
           width: `${frame.w * scale}px`,
           height: `${frame.h * scale}px`
         }}>
      <div className="pop-frame__inner"
           style={{
             width: `${frame.w}px`,
             height: `${frame.h}px`,
             transform: `scale(${scale})`,
             transformOrigin: 'top left'
           }}>
        {/* 가짜 쇼핑몰 배경 */}
        <div className="pop-frame__shop">
          <div className="pop-frame__shopHeader"/>
          <div className="pop-frame__shopGrid">
            {Array.from({length: 6}).map((_, i) => (
              <div key={i} className="pop-frame__shopCard"/>
            ))}
          </div>
        </div>
        {/* 팝업 */}
        {popup && popup.type && <PopupBody popup={popup}/>}
        {/* 모달/풀스크린 백드롭 */}
        {popup && (popup.type === 'modal' || popup.type === 'fullscreen') && (
          <div className="pop-frame__backdrop"/>
        )}
      </div>
    </div>
  );
};

export default DeviceFrame;
